import { Category } from "./Categories";
import { useAllergies } from "../../hooks/useAllergies";
import { usePatients } from "../../hooks/usePatients";
import { useImplants } from "../../hooks/useImplants";
import { useMedicines } from "../../hooks/useMedicines";
import { useMedicineDoses } from "../../hooks/useMedicineDoses";
import { useSurgeryTypes } from "../../hooks/useSurgeryTypes";
import { useDocumentTypes } from "../../hooks/useDocumentTypes";
import { useRecordCategories } from "../../hooks/useRecordCategories";
import { useRehabFacilities } from "../../hooks/useRahabFacilties";
import { useUsers } from "../../hooks/useUsers";
import { useDiseases } from "../../hooks/useDiseases";
import { useDoctors } from "../../hooks/useDoctor";
import { useSpecialties } from "../../hooks/useSpecialties";
import { doctorFields } from "../../types/doctor";
import { useRvs } from "../../hooks/useRv";

export const useEntityAdapter = (category: Category) => {
  // 1. Εδώ γίνονται όλα τα imports και τα calls μακριά από τη σελίδα σου
  const allergies = useAllergies();
  const patients = usePatients();
  const implants = useImplants();
  const medicines = useMedicines();
  const medicineDoses = useMedicineDoses();
  const surgeries = useSurgeryTypes();
  const documentTypes = useDocumentTypes();
  const recordCategories = useRecordCategories();
  const rehabFacilities = useRehabFacilities();
  const users = useUsers();
  const diseases = useDiseases();
  const doctors = useDoctors();
  const specialties = useSpecialties();
  const rvs = useRvs();

  // 2. Επιστρέφουμε καθαρά τα δεδομένα ανάλογα με την κατηγορία
  switch (category) {
    case "allergy":
      return {
        data: allergies.allergies,
        isLoading: allergies.isLoading,
        create: allergies.createAllergy,
        remove: allergies.deleteAllergies,
        update: allergies.updateAllergies,
        modalState: allergies.modalState,
        closeModal: allergies.closeModal,
      };
    case "patient":
      return {
        data: patients.patients,
        isLoading: patients.isLoading,
        create: patients.createPatient,
        remove: patients.deletePatients,
        update: patients.updatePatient,
        modalState: patients.modalState,
        closeModal: patients.closeModal,
      };
    case "implant":
      return {
        data: implants.implants,
        isLoading: implants.isLoading,
        create: implants.createImplant,
        remove: implants.deleteImplants,
        update: implants.updateImplant,    
        modalState: implants.modalState,
        closeModal: implants.closeModal,
      };
    case "medicine":
      return {
        data: medicines.medicines,
        isLoading: medicines.isLoading,
        create: medicines.createMedicine,
        remove: medicines.deleteMedicines,
        update: medicines.updateMedicine,
        modalState: medicines.modalState,
        closeModal: medicines.closeModal,
      };

    case "medicineDose":
      return {
        data: medicineDoses.medicineDoses,
        isLoading: medicineDoses.isLoading,
        create: medicineDoses.createMedicineDose,
        remove: medicineDoses.deleteMedicineDoses,
        update: medicineDoses.updateMedicineDose,
        modalState: medicineDoses.modalState,
        closeModal: medicineDoses.closeModal,
      };
    case "surgery":
      return {
        data: surgeries.surgeryTypes,
        isLoading: surgeries.isLoading,
        create: surgeries.createSurgeryType,
        remove: surgeries.deleteSurgeryTypes,
        update: surgeries.updateSurgeryType,
        modalState: surgeries.modalState,
        closeModal: surgeries.closeModal,
      };
    case "documentType":
      return {
        data: documentTypes.documentTypes,
        isLoading: documentTypes.isLoading,
        create: documentTypes.createDocumentType,
        remove: documentTypes.deleteDocumentTypes,
        update: documentTypes.updateDocumentType,
        modalState: documentTypes.modalState,
        closeModal: documentTypes.closeModal,
      };
    case "historyType":
      return {
        data: recordCategories.recordCategories,
        isLoading: recordCategories.isLoading,
        create: recordCategories.createRecordCategory,
        remove: recordCategories.deleteRecordCategories,
        update: recordCategories.updateRecordCategory,
        modalState: recordCategories.modalState,
        closeModal: recordCategories.closeModal,
      };
    case "rehabCenter":
      return {
        data: rehabFacilities.rehabFacilities,
        isLoading: rehabFacilities.isLoading,
        create: rehabFacilities.createRehabFacility,
        remove: rehabFacilities.deleteRehabFacilities,
        update: rehabFacilities.updateRehabFacility,
        modalState: rehabFacilities.modalState,
        closeModal: rehabFacilities.closeModal,
      };
    case "user":
      return {
        data: users.users,
        isLoading: users.isLoading,
        create: users.createUser,
        remove: users.deleteUsers,
        update: users.updateUser,
        modalState: users.modalState,
        closeModal: users.closeModal,
      };
    case "disease":
      return {
        data: diseases.diseases,
        isLoading: diseases.isLoading,
        create: diseases.createDisease,
        remove: diseases.deleteDiseases,
        update: diseases.updateDisease,
        modalState: diseases.modalState,
        closeModal: diseases.closeModal,
      };
    case "rv":
      return {
        data: rvs.rvs,
        isLoading: rvs.isLoading,
        create: rvs.createRv,
        remove: rvs.deleteRvs,
        update: rvs.updateRv,
        modalState: rvs.modalState,
        closeModal: rvs.closeModal,
      };
    case "doctor": {
      const specialtyOptions = (specialties.specialties || []).map((s) => ({
        value: s.specialtyId,
        label: s.specialtyDescr,
      }));
      return {
        data: doctors.doctors,
        isLoading: doctors.isLoading,
        create: doctors.createDoctor,
        remove: doctors.deleteDoctors,
        update: doctors.updateDoctor,
        modalState: doctors.modalState,
        fields: doctorFields(specialtyOptions),
        closeModal: doctors.closeModal,
      };
    }
    case "specialty":
      return {
        data: specialties.specialties,
        isLoading: specialties.isLoading,
        create: specialties.createSpecialty,
        remove: specialties.deleteSpecialties,
        update: specialties.updateSpecialties,
        modalState: specialties.modalState,
        closeModal: specialties.closeModal,
      };
    default:
      return {
        data: [],
        isLoading: false,
        create: async () => {},
        remove: async () => {},
        modalState: { isOpen: false, title: "", message: "", type: "success" as const },
        closeModal: () => {},
        fields: [],
      };
  }
};